/**
 * Compliance schema — single source of truth for F61 regulatory identity fields.
 *
 * Used by:
 *   - @argusai/api    (Zod validation on PATCH /users/:id/identity-fields)
 *   - @argusai/auth   (AuthContext.clearance / hipaaBaaStatus)
 *   - @argusai/web    (Users tab dropdown, create-user dialog, group-editor)
 *
 * The enum values are aligned with US Government clearance levels and 22 CFR
 * 120.15 (US-Person definition). Changing values here requires a DB migration
 * + audit-row backfill + UI re-render. Don't drift these.
 */

import { z } from 'zod';

// ─── Clearance level (5-level US Government scale) ───────────────────────────

export const ClearanceLevelSchema = z.enum([
  'unclassified',
  'cui',
  'secret',
  'ts',
  'ts_sci',
]);
export type ClearanceLevel = z.infer<typeof ClearanceLevelSchema>;

/**
 * Display labels for UI. Keys must stay 1:1 with `ClearanceLevelSchema`.
 */
export const CLEARANCE_LABELS: Record<ClearanceLevel, string> = {
  unclassified: 'Unclassified',
  cui: 'CUI',
  secret: 'Secret',
  ts: 'Top Secret',
  ts_sci: 'TS/SCI',
};

/**
 * Whether a clearance level requires US-Person verification (22 CFR 120.15).
 * Anything above CUI requires verified US-Person status before the user can
 * be assigned the level.
 */
export function clearanceRequiresUsPerson(level: ClearanceLevel): boolean {
  return level !== 'unclassified' && level !== 'cui';
}

// ─── HIPAA BAA status ────────────────────────────────────────────────────────

export const HipaaBaaStatusSchema = z.enum([
  'not_applicable',
  'pending',
  'signed',
  'expired',
]);
export type HipaaBaaStatus = z.infer<typeof HipaaBaaStatusSchema>;

export const HIPAA_BAA_STATUS_LABELS: Record<HipaaBaaStatus, string> = {
  not_applicable: 'Not applicable',
  pending: 'Pending',
  signed: 'Signed',
  expired: 'Expired',
};
